import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Medal, Award, Star } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface LeaderboardEntry {
  user_id: string;
  points: number;
  rank: number;
  username: string;
  display_name?: string;
  avatar_url?: string;
}

const Leaderboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState<number | null>(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      // Get top participants by points from league_participants joined with profiles
      const { data: leaderboardData, error } = await supabase
        .from('league_participants')
        .select(`
          user_id,
          points,
          profiles (
            username,
            display_name,
            avatar_url
          )
        `)
        .order('points', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Calculate ranks and format data
      const formattedData: LeaderboardEntry[] = leaderboardData?.map((entry, index) => {
        const profile = entry.profiles as any;
        return {
          user_id: entry.user_id,
          points: entry.points,
          rank: index + 1,
          username: profile?.username || 'Unknown',
          display_name: profile?.display_name,
          avatar_url: profile?.avatar_url,
        };
      }) || [];

      setLeaderboard(formattedData);

      // Find current user's rank
      if (user) {
        const userEntry = formattedData.find(entry => entry.user_id === user.id);
        setUserRank(userEntry?.rank || null);
      }

    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast({
        title: 'Error loading leaderboard',
        description: 'Please try refreshing the page.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Award className="h-6 w-6 text-amber-600" />;
      default:
        return <Star className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getRankBadgeVariant = (rank: number) => {
    switch (rank) {
      case 1:
        return "default";
      case 2:
        return "secondary";
      case 3:
        return "outline";
      default:
        return "outline";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Trophy className="h-12 w-12 mx-auto text-primary animate-pulse mb-4" />
          <p className="text-muted-foreground">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Trophy className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">Global Leaderboard</h1>
                <p className="text-sm text-muted-foreground">
                  {userRank ? `Your current rank: #${userRank}` : 'Compete to get ranked!'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Trophy className="h-5 w-5 mr-2 text-primary" />
              Top Players
            </CardTitle>
            <CardDescription>
              Rankings based on total points earned across all leagues
            </CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No rankings available yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Be the first to join leagues and start earning points!
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((entry) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-all hover:shadow-md ${
                      entry.user_id === user?.id 
                        ? 'bg-primary/5 border-primary/20' 
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-3">
                        {getRankIcon(entry.rank)}
                        <Badge variant={getRankBadgeVariant(entry.rank)}>
                          #{entry.rank}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        {entry.avatar_url ? (
                          <img
                            src={entry.avatar_url}
                            alt={entry.username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-primary font-semibold">
                              {entry.username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        
                        <div>
                          <p className="font-semibold">
                            {entry.display_name || entry.username}
                            {entry.user_id === user?.id && (
                              <span className="ml-2 text-primary text-sm">(You)</span>
                            )}
                          </p>
                          {entry.display_name && (
                            <p className="text-sm text-muted-foreground">@{entry.username}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-bold text-lg">{entry.points.toFixed(0)}</div>
                      <div className="text-sm text-muted-foreground">points</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Leaderboard;